import {
  CollectionReference,
  DocumentSnapshot,
  endAt,
  endBefore,
  FieldPath,
  limit,
  limitToLast,
  orderBy,
  OrderByDirection,
  query,
  Query,
  startAfter,
  startAt,
  where,
  WhereFilterOp,
} from "firebase/firestore";

/**
 * The type of query operation.
 */
export type QueryOpType =
  | "where"
  | "orderBy"
  | "limit"
  | "limitToLast"
  | "startAt"
  | "startAfter"
  | "endBefore"
  | "endAt";

/**
 * An operation that can be applied to a Firestore query.
 */
export type QueryOp =
  | WhereOp
  | OrderByOp
  | LimitOp
  | LimitToLastOp
  | StartAtOp
  | StartAfterOp
  | EndBeforeOp
  | EndAtOp;

export interface QueryOpBase<T extends any[] = any[]> {
  op: QueryOpType;
  args: T;
}

export interface WhereOp extends QueryOpBase {
  op: "where";
  args: [string | FieldPath, WhereFilterOp, any];
}

export interface OrderByOp extends QueryOpBase {
  op: "orderBy";
  args: [string | FieldPath, OrderByDirection?];
}

export interface LimitOp extends QueryOpBase {
  op: "limit";
  args: [number];
}

export interface LimitToLastOp extends QueryOpBase {
  op: "limitToLast";
  args: [number];
}

export interface StartAtOp<T = unknown> extends QueryOpBase {
  op: "startAt";
  args: [DocumentSnapshot<T>] | any[];
}

export interface StartAfterOp<T = unknown> extends QueryOpBase {
  op: "startAfter";
  args: [DocumentSnapshot<T>] | any[];
}

export interface EndBeforeOp<T = unknown> extends QueryOpBase {
  op: "endBefore";
  args: [DocumentSnapshot<T>] | any[];
}

export interface EndAtOp<T = unknown> extends QueryOpBase {
  op: "endAt";
  args: [DocumentSnapshot<T>] | any[];
}

/**
 * This function takes in a query and applies a list of operations to the query.
 *
 * @param ref The query root reference.
 * @param operations A list of operations to apply to the query.
 */
export const generateQuery = <T>(
  ref: CollectionReference<T> | Query<T>,
  operations: (QueryOp | undefined | null)[]
): Query<T> => {
  for (const operation of operations) {
    switch (operation?.op) {
      case "where": {
        ref = query(ref, where(...operation.args));
        break;
      }
      case "orderBy": {
        ref = query(ref, orderBy(...operation.args));
        break;
      }
      case "limit": {
        ref = query(ref, limit(...operation.args));
        break;
      }
      case "limitToLast": {
        ref = query(ref, limitToLast(...operation.args));
        break;
      }
      case "startAt": {
        ref = query(ref, startAt(...operation.args));
        break;
      }
      case "startAfter": {
        ref = query(ref, startAfter(...operation.args));
        break;
      }
      case "endAt": {
        ref = query(ref, endAt(...operation.args));
        break;
      }
      case "endBefore": {
        ref = query(ref, endBefore(...operation.args));
        break;
      }
    }
  }
  return ref as Query<T>;
};
